import { createBrowserRouter, Navigate } from "react-router";
import TopNav from "./layouts/TopNav";
import { PlatformRole } from "./config/types";
import { AccountSettings } from "./pages/AccountSettings";
import Chart from "./pages/Chart";
import Dashboard from "./pages/Dashboard";
import { LoginContainer } from "./pages/Login";
import { ActivatePasswordComplete } from "./pages/reset-password/ActivatePasswordComplete";
import { EmailSent } from "./pages/reset-password/EmailSent";
import { ForgotPassword } from "./pages/reset-password/ForgotPassword";
import { ResetPassword } from "./pages/reset-password/ResetPassword";
import { ResetPasswordComplete } from "./pages/reset-password/ResetPasswordComplete";
import { RestoreAccount } from "./pages/RestoreAccount";
import TwoFactorAuthPage from "./pages/TwoFactorAuthPage";
import { UserManagement } from "./pages/UserManagement";
import RouteRequireRole from "./layouts/RouteRequireRole";
import RouteAuthorized from "./layouts/RouteAuthorized";
import Page from "./layouts/Page";
import RouteUnauthorized from "./layouts/RouteUnauthorized";
import Office from "./pages/Office";
import { DocumentPage } from "./pages/DocumentPage";
import WorkflowPage from "./pages/WorkflowPage";
import { DocumentAuditTrailPage } from "./pages/DocumentAuditTrailPage";
import WorkflowsPage from "./pages/WorkflowsPage";
import WorkflowTemplatesPage from "./pages/WorkflowTemplatesPage";
import NotFound from "./pages/NotFound";
import { Library } from "./pages/Library";
import OnboardDocuments from "./pages/OnboardDocuments";
import ProjectPage from "./pages/ProjectPage";
import SharePointOnboardingPage from "./pages/SharePointOnboardingPage";
import { SectionsOverview } from "~/pages/SectionsOverview";
import UserGuide from "./pages/UserGuide";
import Support from "./pages/Support";
import AddInLoginCallback from "./pages/addin/AddInLoginCallback";
import OffboardUserPage from "~/pages/OffboardUser";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { featureFlags } from "./config/featureFlags";
import { useUserContext } from "./hooks/useUserContext";
import MicrosoftOnboardingDashboard from "~/pages/MicrosoftOnboardingDashboard";
import ColorPalettePage from "~/devtools/pages/ColorPalettePage";

const SectionsOverviewWrapper = () => {
  const { user } = useUserContext();
  const isEnabled = useFeatureFlagEnabled(featureFlags.genestaSections);
  const isReader = user.role === PlatformRole.reader;

  if (!isEnabled || isReader) return <NotFound />;
  return <SectionsOverview />;
};

const ColorPaletteWrapper = () => {
  const isEnabled = useFeatureFlagEnabled(featureFlags.colorPalette);

  if (!isEnabled) return <NotFound />;
  return <ColorPalettePage />;
};

const router = createBrowserRouter([
  {
    path: "/add-in-callback",
    element: (
      <Page title="Log in success">
        <AddInLoginCallback />
      </Page>
    ),
  },
  {
    path: "/microsoft-onboarding",
    element: (
      <Page title="Microsoft Onboarding">
        <MicrosoftOnboardingDashboard />
      </Page>
    ),
  },
  {
    element: <RouteAuthorized />,
    children: [
      {
        element: <TopNav />,
        children: [
          {
            path: "/",
            element: (
              <Page title="Dashboard">
                <Dashboard />
              </Page>
            ),
          },
          {
            path: "/chart",
            element: (
              <Page title="Chart">
                <Chart />
              </Page>
            ),
          },
          {
            path: "/documents",
            element: (
              <Page title="Library">
                <Library />
              </Page>
            ),
          },
          {
            path: "/user-guide",
            element: (
              <Page title="User guide">
                <UserGuide />
              </Page>
            ),
          },
          {
            // Deprecated route, kept for backward compatibility
            path: "/library",
            element: <Navigate to="/documents" replace />,
          },
          {
            path: "/sections",
            element: (
              <Page title="Sections">
                <SectionsOverviewWrapper />
              </Page>
            ),
          },
          {
            element: (
              <RouteRequireRole
                allowedRoles={[
                  PlatformRole.superAdmin,
                  PlatformRole.contributor,
                  PlatformRole.coordinator,
                ]}
              />
            ),
            children: [
              {
                path: "/users",
                element: (
                  <Page title="User management">
                    <UserManagement />
                  </Page>
                ),
              },
              {
                path: "/users/:id/offboard",
                element: (
                  <Page title="Offboard User">
                    <OffboardUserPage />
                  </Page>
                ),
              },
            ],
          },
          {
            path: "/account-settings",
            element: (
              <Page title="Account settings">
                <AccountSettings />
              </Page>
            ),
          },
          {
            path: "/set-up-2fa",
            element: (
              <Page title="Set up 2FA">
                <TwoFactorAuthPage />
              </Page>
            ),
          },
          {
            path: "/support",
            element: (
              <Page title="Support">
                <Support />
              </Page>
            ),
          },
          {
            path: "/dev/colors",
            element: (
              <Page title="Color Palette">
                <ColorPaletteWrapper />
              </Page>
            ),
          },
          {
            path: "documents/:id",
            element: <DocumentPage />,
          },
          {
            path: "projects/:projectId",
            element: <ProjectPage />,
          },
          {
            path: "documents/:id/audit-trail",
            element: (
              <Page title="Audit Trail">
                <DocumentAuditTrailPage />
              </Page>
            ),
          },

          {
            path: "workflows",
            element: (
              <Page title="Workflows">
                <WorkflowsPage />
              </Page>
            ),
          },
          {
            path: "workflows/:id/edit",
            element: (
              <Page title="Edit Workflow">
                <WorkflowPage />
              </Page>
            ),
          },
          {
            element: (
              <RouteRequireRole
                allowedRoles={[
                  PlatformRole.coordinator,
                  PlatformRole.superAdmin,
                ]}
              />
            ),
            children: [
              {
                path: "workflow-templates",
                element: (
                  <Page title="Workflow Templates">
                    <WorkflowTemplatesPage />
                  </Page>
                ),
              },
            ],
          },
          {
            element: (
              <RouteRequireRole
                allowedRoles={[
                  PlatformRole.contributor,
                  PlatformRole.coordinator,
                  PlatformRole.superAdmin,
                ]}
              />
            ),
            children: [
              {
                path: "/onboarding/documents",
                element: (
                  <Page title="Onboard Documents">
                    <OnboardDocuments />
                  </Page>
                ),
              },
            ],
          },
          {
            path: "/onboarding/sp-mirroring",
            element: (
              <Page title="SharePoint Integration">
                <SharePointOnboardingPage />
              </Page>
            ),
          },
          {
            path: "*",
            element: (
              <Page title="404">
                <NotFound />
              </Page>
            ),
          },
        ],
      },
      {
        // Deprecated route, kept for backward compatibility
        path: "document/:id/edit",
        element: <Office />,
      },
      {
        path: "versions/:id/editor",
        element: <Office />,
      },
    ],
  },
  {
    element: <RouteUnauthorized />,
    children: [
      {
        // Deprecated route, kept for backward compatibility
        path: "/activate-account",
        element: (
          <Page title="Activate account">
            <ResetPassword />
          </Page>
        ),
      },
      {
        path: "/activate-account-complete",
        element: (
          <Page title="Activate account completed">
            <ActivatePasswordComplete />
          </Page>
        ),
      },
      {
        path: "/email-sent",
        element: (
          <Page title="Email sent">
            <EmailSent />
          </Page>
        ),
      },
      {
        path: "/forgot-password",
        element: (
          <Page title="Forgot password">
            <ForgotPassword />
          </Page>
        ),
      },
      {
        path: "/login",
        element: (
          <Page title="Sign in">
            <LoginContainer />
          </Page>
        ),
      },
      {
        path: "/restore-account",
        element: (
          <Page title="Restore account">
            <RestoreAccount />
          </Page>
        ),
      },
      {
        path: "/reset-password/:token?",
        element: (
          <Page title="Reset password">
            <ResetPassword />
          </Page>
        ),
      },
      {
        path: "/reset-password-complete",
        element: (
          <Page title="Reset password complete">
            <ResetPasswordComplete />
          </Page>
        ),
      },
    ],
  },
]);

export default router;
